import {
  FcBuilder,
  FcCenterText,
  FcColor,
  FcColorBlack,
  FcColorGradient,
  FcColorTransparent,
  FcColorWhite,
  FcContainer,
  FcGeneratorOptions,
  FcGeneratorPositionElementOptions,
  FcGrid,
  FcGridProps,
  FcLogo,
  FcOverlay,
  FcPosition,
  FcPositionElement,
  FcPositionProperty,
  FcPreconfiguredStyle,
  FcRandomShapeColorType,
  FcRandomShapes,
  FcRenderer,
  FcShape,
  FcSvg,
  FcText,
  defaultPositionElementBottomLeft,
  defaultPositionElementTopLeft,
  defaultPositionElementTopRight,
  defaultPositionText,
  fcContainerIsCustom,
  fcContainerIsCustomerFromOpts,
  optionsDefaults,
  optionsInset,
  optionsInsetExtend,
  optionsRim
} from '@dtx-company/flowcode-generator-types/src'
import { deepCopy, mergeDeep } from './fc_utils'
import { fcBuildFlowcode } from './fc_builder'
import { fcGetStudioConfig } from './fc_images'
import { fcLoadFonts } from './fc_fonts'
import { fcRenderFlowcode } from './fc_renderer'

export async function fcGenerateFlowcode(
  options: FcGeneratorOptions,
  cmyk?: boolean,
  customBuilder?: FcBuilder,
  customRenderer?: FcRenderer,
  dataCheckOnly?: boolean
): Promise<FcSvg> {
  if (!options.data) {
    throw new Error('Data to encode cannot be empty')
  }

  if (options.baseStudioConfigId) {
    const baseConfig = await fcGetStudioConfig(options.baseStudioConfigId)
    options = mergeDeep(deepCopy(baseConfig), options)
  }
  const builder: FcBuilder = customBuilder || fcBuildFlowcode
  const renderer: FcRenderer = customRenderer || fcRenderFlowcode

  if (options.defaultColor) {
    if (!options.containerOuterPatternColor || options.alwaysUseDefaultColor) {
      options.containerOuterPatternColor = options.defaultColor
    }
    if (!options.containerBorderColor || options.alwaysUseDefaultColor) {
      options.containerBorderColor = options.defaultColor
    }
    if (!options.gridModuleColor || options.alwaysUseDefaultColor) {
      options.gridModuleColor = options.defaultColor
    }
    if (!options.containerRimBorderColor || options.alwaysUseDefaultColor) {
      options.containerRimBorderColor = options.defaultColor
    }
    if (options.containerText) {
      options.containerText.forEach(text => {
        setDefaultColorText(text, options.defaultColor!, options.alwaysUseDefaultColor)
      })
    }
    if (options.containerRimText) {
      options.containerRimText.forEach(text => {
        setDefaultColorText(text, options.defaultColor!, options.alwaysUseDefaultColor)
      })
    }
    if (options.containerRimText === undefined && options.style === FcPreconfiguredStyle.RIM) {
      options.containerRimText = deepCopy(optionsRim.containerRimText)
      if (options.containerRimText) {
        options.containerRimText.forEach(t => {
          t.fontColor = options.defaultColor!
        })
      }
    }
    if (options.containerText === undefined) {
      options.containerText = deepCopy(getDefault(options).containerText)
      if (options.containerText) {
        options.containerText.forEach(t => {
          t.fontColor = options.defaultColor!
        })
      }
    }

    options.positionElementBottomLeft = setDefaultColorPositionElement(
      options.positionElementBottomLeft,
      options.defaultColor,
      defaultPositionElementBottomLeft.text,
      options.alwaysUseDefaultColor
    )
    options.positionElementTopLeft = setDefaultColorPositionElement(
      options.positionElementTopLeft,
      options.defaultColor,
      defaultPositionElementTopLeft.text,
      options.alwaysUseDefaultColor
    )
    options.positionElementTopRight = setDefaultColorPositionElement(
      options.positionElementTopRight,
      options.defaultColor,
      defaultPositionElementTopRight.text,
      options.alwaysUseDefaultColor
    )
  }
  if (options.oldschool) {
    const eye: FcGeneratorPositionElementOptions = {
      outerShape: FcShape.SQUARE,
      innerShape: FcShape.SQUARE
    }
    options.containerShape = FcShape.NONE
    options.dataGridPercentageSize = 100
    options.gridModuleShape = FcShape.SQUARE
    options.positionElementTopRight = eye
    options.positionElementTopLeft = eye
    options.containerEmptySpaceSize = 0
  }

  options = populateDefaults(options)

  if (options.qrrotate) {
    applyDefaultTextToRotatedCodes(options)
  }

  if (options.transparent) {
    options.gridBackgroundColor = FcColorTransparent
    options.containerBackgroundColor = FcColorTransparent
    options.containerRimBackgroundColor = FcColorTransparent
    if (options.positionElementTopLeft) {
      options.positionElementTopLeft.backgroundColor = FcColorTransparent
    }
    if (options.positionElementBottomLeft) {
      options.positionElementBottomLeft.backgroundColor = FcColorTransparent
    }
    if (options.positionElementTopRight) {
      options.positionElementTopRight.backgroundColor = FcColorTransparent
    }
  } else if (options.defaultBackgroundColor) {
    options.gridBackgroundColor = options.defaultBackgroundColor
    options.containerBackgroundColor = options.defaultBackgroundColor
    options.containerRimBackgroundColor = options.defaultBackgroundColor
    if (options.positionElementTopLeft) {
      options.positionElementTopLeft.backgroundColor = options.defaultBackgroundColor
    }
    if (options.positionElementBottomLeft) {
      options.positionElementBottomLeft.backgroundColor = options.defaultBackgroundColor
    }
    if (options.positionElementTopRight) {
      options.positionElementTopRight.backgroundColor = options.defaultBackgroundColor
    }
  }

  let containerColorGradient: FcColorGradient | undefined
  if (options.gridModuleColorGradient) {
    options.gridModuleColor = FcColorWhite
    options.containerOuterPatternColor = FcColorWhite
    if (options.gridModuleImageUrl) {
      options.gridModuleColorFillSvg = true
    }
    if (options.containerOuterPatternImageUrl) {
      options.containerOuterPatternColorFillSvg = true
    }
    containerColorGradient = options.gridModuleColorGradient
  }

  if (options.containerMaskImageUrl) {
    options.gridModuleColor = FcColorWhite
    options.containerOuterPatternColor = FcColorWhite
    makePositionElementMask(options.positionElementTopLeft)
    makePositionElementMask(options.positionElementTopRight)
    makePositionElementMask(options.positionElementBottomLeft)
    if (options.containerText) {
      options.containerText.forEach(txt => {
        txt.fontColor = FcColorWhite
      })
    }
    if (options.gridModuleImageUrl) {
      options.gridModuleColorFillSvg = true
    }
    if (options.containerOuterPatternColorFillSvg) {
      options.containerOuterPatternColorFillSvg = true
    }
    makeRandomShapesMask(options.containerOuterPatternRandomShapes)
    makeRandomShapesMask(options.gridModuleRandomShapes)
  }

  // seed for data pattern color
  const seed = options.data
  const gridProps = new FcGridProps(
    false,
    options.gridModuleColor!,
    options.gridBackgroundColor!,
    options.gridModuleShape,
    options.gridGradient,
    options.gridModuleImageUrl,
    options.gridModuleRandomSizes,
    options.gridModuleColorFillSvg,
    options.gridModulePipes,
    options.gridModuleRandomShapes,
    options.logoInCodePixelsShape,
    options.logoInCodeColor,
    seed
  )
  const contProps = new FcGridProps(
    true,
    options.containerOuterPatternColor!,
    options.containerBackgroundColor!,
    options.containerOuterPatternShape,
    options.containerOuterPatternGradient,
    options.containerOuterPatternImageUrl,
    options.containerOuterPatternRandomSizes,
    options.containerOuterPatternColorFillSvg,
    options.containerOuterPatternPipes,
    options.containerOuterPatternRandomShapes,
    undefined,
    undefined,
    seed
  )
  const grid = new FcGrid(
    options.dataGridPercentageSize!,
    gridProps,
    contProps,
    options.errorCorrectionLevel!,
    options.containerEmptySpaceSize!,
    !fcContainerIsCustomerFromOpts(options) ||
      options.customContainerOuterPattern ||
      (options.overlays?.length || 0) > 0,
    options.data || '',
    options.radiusMask,
    options.qrdensity,
    options.qrrotate,
    options.erasedExact,
    options.erasedExactTolerance,
    options.blendErasurePad === undefined ? 1 : options.blendErasurePad,
    options.data || ''
  )
  const container: FcContainer = {
    dataCheckOnly,
    maskImageUrl: options.containerMaskImageUrl,
    containerSquareRadius:
      options.containerSquareRadius === undefined ? 15 : options.containerSquareRadius,
    shape: options.containerShape!,
    text: options.containerText,
    fragmentRadiusMask: options.containerFragmentRadiusMask,
    overlayRadiusMask: options.overlayRadiusMask,
    overlayExpandTargetSize: options.overlayExpandTargetSize,
    borderColor: options.containerBorderColor!,
    borderOutlineColor: options.containerBorderOutlineColor,
    borderOutlineSize: options.containerBorderOutlineSize,
    borderSize: options.containerBorderSize!,
    borderGradient: options.containerBorderGradient,
    outerPatternColor: options.containerOuterPatternColor!,
    outerPatternShape: options.containerOuterPatternShape!,
    outerPatternImageUrl: options.containerOuterPatternImageUrl,
    backgroundColor: options.containerBackgroundColor!,
    dataGridPercentageSize: options.dataGridPercentageSize!,
    containerImageUrl: options.containerImageUrl,
    containerImageUrlFillSvg: options.containerImageUrlFillSvg,
    containerImageUrlFg: options.containerImageUrlFg,
    containerImageUrlOpacity: options.containerImageUrlOpacity,
    containerImageUrlFgOpacity: options.containerImageUrlFgOpacity,
    containerImageOverlayPrivacy: options.containerImageOverlayPrivacy,
    containerImageBackgroundOnly: options.containerImageBackgroundOnly,
    rimWidth: options.containerRimWidth,
    rimText: options.containerRimText,
    rimPatternSpacing: options.containerRimPatternSpacing,
    rimImageUrl: options.containerRimImageUrl,
    rimImageUrlOpacity: options.containerRimImageUrlOpacity,
    rimBackgroundColor: options.containerRimBackgroundColor,
    rimBorderColor: options.containerRimBorderColor,
    rimBorderSize: options.containerRimBorderSize,
    rimOutlineColor: options.containerRimOutlineColor,
    rimOutlineSize: options.containerRimOutlineSize,
    rimBorderGradient: options.containerRimBorderGradient,
    rimPatternColorFillSvg: options.containerRimPatternColorFillSvg,
    rimPatternColor: options.containerRimPatternColor,
    rimPatternShape: options.containerRimPatternShape,
    rimPatternImageUrl: options.containerRimPatternImageUrl,
    rimPatternSize: options.containerRimPatternSize,
    borderInsetEyes: options.containerBorderInsetEyes,
    backgroundInsetExtend: options.containerBackgroundInsetExtend,
    borderInsetCalcValue: 0,
    privacyTextPaddingHorizontal: options.privacyTextPaddingHorizontal,
    privacyTextPaddingVertical: options.privacyTextPaddingVertical,
    useCmyk: cmyk || false,
    fragments: options.containerFragments,
    fragmentGradient: options.containerFragmentGradient,
    containerColorGradient
  }
  const positionElements: FcPositionElement[] = [
    createPositionElement(
      new FcPosition(FcPositionProperty.BOTTOM_LEFT, 0),
      options.positionElementBottomLeft!
    ),
    createPositionElement(
      new FcPosition(FcPositionProperty.TOP_LEFT, 0),
      options.positionElementTopLeft!
    ),
    createPositionElement(
      new FcPosition(FcPositionProperty.TOP_RIGHT, 0),
      options.positionElementTopRight!
    )
  ]

  let logo: FcLogo | undefined = undefined
  if (options.logoImageUrl || options.logoInCodeBitmap) {
    logo = {
      imgUrl: options.logoImageUrl || '',
      width: options.logoWidth!,
      height: options.logoHeight!,
      fillColor: options.logoFillSvgColor,
      logoModulePadding: options.logoModulePadding,
      outerPadding: options.logoOuterPadding,
      mask: options.logoModuleMask,
      blend: options.logoBlend,
      blendErase: options.logoBlendErase,
      preserveAspect: options.logoPreserveAspect,
      blendModuleSize: options.logoBlendModuleSize ? options.logoBlendModuleSize / 100 : 0.4,
      blendModuleColorFg:
        options.logoBlendModuleColorFg || getFirsString(options.gridModuleColor) || FcColorBlack,
      blendModuleColorBg:
        options.logoBlendModuleColorBg || options.gridBackgroundColor || FcColorWhite,
      blendModuleShape: options.logoBlendModuleShape || FcShape.CIRCLE,
      blendModuleImageUrl: options.logoBlendModuleImageUrl,
      blendModuleTolerationFg: options.logoBlendModuleTolerationFg || 0,
      blendModuleTolerationBg: options.logoBlendModuleTolerationBg || 0,
      blendModulePixelsToCheck: options.logoBlendModulePixelsToCheck || 2,
      rotate: options.logoRotate,
      inCode: options.logoInCode || options.logoInCodePixels,
      inCodePixels: options.logoInCodePixels,
      inCodeBitmap: options.logoInCodeBitmap
    }
  }
  const overlays: FcOverlay[] = []
  if (options.overlays) {
    options.overlays.forEach(overlay => {
      overlays.push({
        width: overlay.width,
        degrees: overlay.degrees,
        radius: overlay.radius,
        imgUrl: overlay.imageUrl,
        fillColor: overlay.fillSvgColor,
        maskUrl: overlay.maskUrl,
        clip: overlay.clip || false,
        blend: options.overlayBlend,
        blendErase: options.overlayBlendErase,
        padding: overlay.padding,
        rotate: overlay.rotate,
        blendModuleColorFg:
          options.overlayBlendModuleColorFg ||
          getFirsString(options.gridModuleColor) ||
          FcColorBlack,
        blendModuleColorBg:
          options.overlayBlendModuleColorBg || options.gridBackgroundColor || FcColorWhite,
        blendModuleShape: options.overlayBlendModuleShape || FcShape.CIRCLE,
        blendModuleImageUrl: options.overlayBlendModuleImageUrl,
        blendModuleSize: options.overlayBlendModuleSize
          ? options.overlayBlendModuleSize / 100
          : 0.4,
        blendModuleTolerationFg: options.overlayBlendModuleTolerationFg || 0,
        blendModuleTolerationBg: options.overlayBlendModuleTolerationBg || 0,
        blendModulePixelsToCheck: options.overlayBlendModulePixelsToCheck || 2
      })
    })
  }

  let centerText: FcCenterText | undefined = undefined
  if (options.centerText) {
    centerText = {
      text: options.centerText,
      paddingHorizontal: options.centerTextPadding || 0,
      paddingVertical: options.centerTextPadding || 0,
      blend: options.centerTextBlend,
      blendErase: options.centerTextBlendErase,
      blendModuleSize: options.centerTextBlendModuleSize
        ? options.centerTextBlendModuleSize / 100
        : 0.4,
      blendModuleColorFg:
        options.centerTextBlendModuleColorFg ||
        getFirsString(options.gridModuleColor) ||
        FcColorBlack,
      blendModuleColorBg:
        options.centerTextBlendModuleColorBg || options.gridBackgroundColor || FcColorWhite,
      blendModuleShape: options.centerTextBlendModuleShape || FcShape.CIRCLE
    }
  }

  let margin = options.margin
  if (
    margin === undefined &&
    container.shape !== FcShape.SQUARE &&
    !fcContainerIsCustom(container)
  ) {
    margin = options.containerBorderSize
  }
  builder(grid.data, grid, logo?.inCode)
  await fcLoadFonts(textNodes(options))
  return renderer(
    container,
    positionElements,
    grid,
    options.targetSize!,
    logo,
    margin,
    centerText,
    overlays
  )
}

function setDefaultColorPositionElement(
  pos: FcGeneratorPositionElementOptions | undefined,
  color: FcColor,
  defaultText: FcText | undefined,
  override?: boolean
): FcGeneratorPositionElementOptions {
  if (!pos) {
    pos = {}
  }
  if (!pos.outerShapeColor || override) {
    pos.outerShapeColor = color
  }
  if (!pos.innerShapeColor || override) {
    pos.innerShapeColor = color
  }
  if (pos.text) {
    setDefaultColorText(pos.text, color, override)
  } else if (defaultText) {
    pos.text = mergeDeep(defaultText)
    pos.text!.fontColor = color
  }
  return pos
}

function getFirsString(str: string | undefined): string | undefined {
  if (!str || str.indexOf(',') === -1) {
    return str
  }
  return str.split(',')[0]
}

function setDefaultColorText(text: FcText | undefined, color: FcColor, override?: boolean): void {
  if (!text) {
    return
  }
  if (!text.fontColor || override) {
    text.fontColor = color
  }
}

function makeRandomShapesMask(randomShapes?: FcRandomShapes): void {
  if (!randomShapes) {
    return
  }
  randomShapes.colors.forEach(color => {
    color.color = FcColorWhite
    color.clusterColorType = FcRandomShapeColorType.SOLID
  })
}

function getDefault(options: FcGeneratorOptions): FcGeneratorOptions {
  if (options.style === FcPreconfiguredStyle.INSET) {
    if (options.defaultBackgroundColor) {
      return optionsInsetExtend
    } else {
      return optionsInset
    }
  } else if (options.style === FcPreconfiguredStyle.RIM) {
    return optionsRim
  }
  return optionsDefaults
}

function populateDefaults(options: FcGeneratorOptions): FcGeneratorOptions {
  return mergeDeep(deepCopy(getDefault(options)), options)
}
/*
  Eyes that are rotated will have branding on the top right position element. 
  That element does not have text by default so we need to apply it. 
*/
function applyDefaultTextToRotatedCodes(options: FcGeneratorOptions): void {
  if (
    options.positionElementTopRight &&
    !options.positionElementTopRight?.text &&
    !options.positionElementTopRight?.imageUrl &&
    options.positionElementTopRight.outerShape === FcShape.LOGO
  ) {
    options.positionElementTopRight.text = defaultPositionText
    if (
      options.positionElementBottomLeft?.outerShape === FcShape.LOGO &&
      !options.positionElementBottomLeft?.imageUrl
    ) {
      options.positionElementBottomLeft.outerShape = FcShape.SQUARE
    }
  }
}

function createPositionElement(
  property: FcPosition,
  options: FcGeneratorPositionElementOptions
): FcPositionElement {
  return {
    position: property,
    outerShape: options.outerShape!,
    innerShape: options.innerShape!,
    backgroundColor: options.backgroundColor!,
    outerShapeColor: options.outerShapeColor!,
    innerShapeColor: options.innerShapeColor!,
    outerShapeOutlineColor: options.outerShapeOutlineColor,
    innerShapeOutlineColor: options.innerShapeOutlineColor,
    outerShapeOutlineSize: options.outerShapeOutlineSize,
    innerShapeOutlineSize: options.innerShapeOutlineSize,
    innerShapeWidth: options.innerShapeWidth!,
    outerShapeWidth: options.outerShapeWidth!,
    text: options.text,
    imageUrl: options.imageUrl,
    innerImageUrl: options.innerImageUrl,
    gradient: options.gradient,
    fillSvg: options.fillSvg,
    insetBorderPadding: options.insetBorderPadding
  }
}

function textNodes(options: FcGeneratorOptions): FcText[] {
  return [
    options.centerText,
    options.containerRimText,
    options.containerText,
    options.positionElementBottomLeft?.text,
    options.positionElementTopLeft?.text,
    options.positionElementTopRight?.text
  ]
    .flat()
    .filter((text: any): text is FcText => !!text)
}

function makePositionElementMask(element?: FcGeneratorPositionElementOptions): void {
  if (!element) {
    return
  }
  element.backgroundColor = FcColorBlack
  element.outerShapeColor = FcColorWhite
  element.innerShapeColor = FcColorWhite
  if (element.text) {
    element.text.fontColor = FcColorWhite
  }
}
